import PendingTaskShortcuts from 'components/organisms/pending-task-shortcuts'
import SellerOutboundReturnsApiV2 from 'api/v2/admins/seller-outbound-returns-api-v2'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import RedirectButton from 'components/atoms/redirect-button'
import TitlePage from 'components/atoms/title-page'
import { STATUS } from 'domains/seller-outbound-return/constants'
import SellerOutboundReturnTable from 'domains/seller-outbound-return/organisms/table'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, Input } from 'reactstrap'
import { ALERT_TYPES } from 'constants/alert-constants'
import { useHistory, useLocation } from 'react-router'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const CONSIGNOR_RETURN_LIST_FILTER_KEYS = [ 'status', 'return_type' ]

const getConsignorReturnListUrlState = (search = '') => {
  const searchParams = new URLSearchParams(search)
  const page = Math.max(Number(searchParams.get('page') || 1), 1)
  const pageSize = Math.max(Number(searchParams.get('per_page') || 10), 1)

  return {
    q         : searchParams.get('q') || '',
    pageIndex : page - 1,
    pageSize,
    filters   : CONSIGNOR_RETURN_LIST_FILTER_KEYS
      .map((filterKey) => ({ id: filterKey, value: searchParams.get(filterKey) }))
      .filter((filter) => filter.value)
  }
}

const syncConsignorReturnListUrl = ({ history, pathname, currentSearch, q, filters, pageIndex, pageSize }) => {
  const searchParams = new URLSearchParams(currentSearch)
  const keyword = q.trim()

  if (keyword) searchParams.set('q', keyword)
  else searchParams.delete('q')

  CONSIGNOR_RETURN_LIST_FILTER_KEYS.forEach((filterKey) => searchParams.delete(filterKey))
  filters.forEach((filter) => {
    if (!CONSIGNOR_RETURN_LIST_FILTER_KEYS.includes(filter.id) || !filter.value) return

    searchParams.set(filter.id, filter.value)
  })

  searchParams.set('page', String(pageIndex + 1))
  searchParams.set('per_page', String(pageSize))

  history.replace({
    pathname,
    search: `?${searchParams.toString()}`
  })
}

const SellerOutboundReturnListPage = ({ pageUtils }) => {
  const history = useHistory()
  const location = useLocation()
  const initialUrlState = getConsignorReturnListUrlState(location.search)
  const pendingTask = new URLSearchParams(location.search).get('pending_task') || undefined

  const [ sellerOutboundReturn, setSellerOutboundReturn ] = useState([])
  const [ pagination, setPagination ]                     = useState({})
  const [ isLoading, setIsLoading ]                       = useState(true)
  const [ isDownloadingCsv, setIsDownloadingCsv ]        = useState(false)
  const [ searchKeyword, setSearchKeyword ]               = useState(() => initialUrlState.q)
  const [ submittedSearchKeyword, setSubmittedSearchKeyword ] = useState(() => initialUrlState.q)
  const [ tableFilters, setTableFilters ]                 = useState(() => initialUrlState.filters)
  const [ initialPageIndex, setInitialPageIndex ]         = useState(() => initialUrlState.pageIndex)
  const [ initialPageSize, setInitialPageSize ]           = useState(() => initialUrlState.pageSize)
  const [ tableVersion, setTableVersion ]                 = useState(0)
  const [ cancelTarget, setCancelTarget ]                 = useState(null)
  const [ isCancelModalOpen, setIsCancelModalOpen ]       = useState(false)
  const [ cancelWarningStep, setCancelWarningStep ]       = useState(1)

  useEffect(() => {
    const next = getConsignorReturnListUrlState(location.search)
    setTableFilters(next.filters)
    setInitialPageIndex(0)
    setInitialPageSize(next.pageSize)
    setSearchKeyword(next.q)
    setSubmittedSearchKeyword(next.q)
    setTableVersion((value) => value + 1)
  }, [pendingTask]) // eslint-disable-line react-hooks/exhaustive-deps

  const isProductCuratedRoute = location.pathname.startsWith('/app/product-curated-team')

  const routeBasePath = isProductCuratedRoute
    ? '/app/product-curated-team/consignor-returns'
    : '/app/seller-outbound-returns'
  const canCancelConsignorReturn = isProductCuratedRoute

  const handleFetchSellerOutboundReturn = useCallback((tableState = {}) => {
    const filters = tableState.filters || []
    const pageIndex = Number.isInteger(tableState.pageIndex) ? tableState.pageIndex : 0
    const pageSize = Number.isInteger(tableState.pageSize) ? tableState.pageSize : 10
    const normalizedTableState = {
      ...tableState,
      filters,
      pageIndex,
      pageSize
    }

    setTableFilters(filters)
    setInitialPageIndex(pageIndex)
    setInitialPageSize(pageSize)

    syncConsignorReturnListUrl({
      history,
      pathname      : window.location.pathname,
      currentSearch : window.location.search,
      q             : submittedSearchKeyword,
      filters,
      pageIndex,
      pageSize
    })

    setIsLoading(true)

    const additionalFilters = submittedSearchKeyword
      ? { q: submittedSearchKeyword }
      : {}

    additionalFilters.pending_task = pendingTask
    SellerOutboundReturnsApiV2.get({
      tableState: normalizedTableState,
      additionalFilters
    })
      .then((response) => {
        setSellerOutboundReturn(response.data.seller_returns)
        setPagination(response.data.meta)
      })
      .catch((error) => {
        const errorResponse = error?.response?.data || {
          messages: {
            base: error?.message || 'Unable to fetch consignor returns'
          }
        }

        pageUtils?.setApiErrorMsg?.(errorResponse)
      })
      .finally(() => setIsLoading(false))
  }, [ history, pageUtils, submittedSearchKeyword, pendingTask ])

  const resetTableToFirstPage = useCallback(({ q, filters }) => {
    setInitialPageIndex(0)

    syncConsignorReturnListUrl({
      history,
      pathname      : window.location.pathname,
      currentSearch : window.location.search,
      q,
      filters,
      pageIndex     : 0,
      pageSize      : initialPageSize
    })

    setTableVersion((currentVersion) => currentVersion + 1)
  }, [ history, initialPageSize ])

  const handleSubmitSearch = useCallback((event) => {
    event.preventDefault()

    const keyword = searchKeyword.trim()

    setSubmittedSearchKeyword(keyword)
    resetTableToFirstPage({ q: keyword, filters: tableFilters })
  }, [ resetTableToFirstPage, searchKeyword, tableFilters ])

  const handleResetSearch = useCallback(() => {
    setSearchKeyword('')
    setSubmittedSearchKeyword('')
    setTableFilters([])
    resetTableToFirstPage({ q: '', filters: [] })
  }, [ resetTableToFirstPage ])

  const downloadBlobResponse = useCallback((response, fallbackFilename, mimeType) => {
    const blob = new Blob([ response.data ], { type: mimeType })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    const contentDisposition = response.headers?.['content-disposition'] || ''
    const matchedFilename = contentDisposition.match(/filename="?([^";]+)"?/)

    link.href = url
    link.download = matchedFilename?.[1] || fallbackFilename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }, [])

  const handleDownloadCsv = useCallback(() => {
    setIsDownloadingCsv(true)

    const additionalFilters = tableFilters.reduce((filters, filter) => {
      if (filter.value) filters[filter.id] = filter.value
      return filters
    }, submittedSearchKeyword ? { q: submittedSearchKeyword } : {})

    SellerOutboundReturnsApiV2.downloadCsv({ additionalFilters })
      .then((response) => {
        downloadBlobResponse(
          response,
          'consignor_returns.csv',
          'text/csv;charset=utf-8'
        )
      })
      .catch((error) => {
        const errorResponse = error?.response?.data || {
          messages: {
            base: error?.message || 'Unable to download consignor return CSV'
          }
        }

        pageUtils?.setApiErrorMsg?.(errorResponse)
      })
      .finally(() => setIsDownloadingCsv(false))
  }, [ downloadBlobResponse, pageUtils, submittedSearchKeyword, tableFilters ])

  const handleSelectRow = useCallback((datum) => {
    const sellerReturnId = datum.seller_return_id || datum.id

    if (!sellerReturnId) return

    history.push({
      pathname: `${routeBasePath}/${sellerReturnId}`
    })
  }, [ history, routeBasePath ])

  const sellerReturnParentStatus = useCallback((datum) => datum?.parent_status || datum?.status, [])

  const isCancelButtonDisabled = useCallback((datum) => (
    ![
      STATUS.PENDING,
      STATUS.ACCEPTED,
      STATUS.SCHEDULED,
      STATUS.SELLER_RECEIVED
    ].includes(sellerReturnParentStatus(datum))
  ), [ sellerReturnParentStatus ])

  const isPostShipmentCancel = useCallback((datum) => (
    [ STATUS.SCHEDULED, STATUS.SELLER_RECEIVED ].includes(sellerReturnParentStatus(datum))
  ), [ sellerReturnParentStatus ])

  const handleCancelModalToggle = useCallback(() => {
    setIsCancelModalOpen((currentState) => {
      if (currentState) {
        setCancelTarget(null)
        setCancelWarningStep(1)
      }

      return !currentState
    })
  }, [])

  const handleCancelClick = useCallback((datum) => {
    setCancelTarget(datum)
    setCancelWarningStep(1)
    setIsCancelModalOpen(true)
  }, [])

  const handleCancelAbort = useCallback(() => {
    const code = String(cancelTarget?.code || '-').replace(/^#/, '')

    pageUtils?.setAlertMsg?.(
      `Cancel Consignor Return #${code} dibatalkan.`,
      ALERT_TYPES.DANGER
    )
  }, [ cancelTarget, pageUtils ])

  const handleCancelConfirm = useCallback(() => {
    if (isPostShipmentCancel(cancelTarget) && cancelWarningStep < 3) {
      setCancelWarningStep((currentStep) => currentStep + 1)
      return
    }

    const sellerReturnId = cancelTarget?.seller_return_id || cancelTarget?.id

    if (!sellerReturnId) return

    setIsLoading(true)

    SellerOutboundReturnsApiV2.cancel({ id: sellerReturnId })
      .then(() => {
        setSellerOutboundReturn((currentRows) => currentRows.map((row) => {
          const rowSellerReturnId = row.seller_return_id || row.id

          if (String(rowSellerReturnId) !== String(sellerReturnId)) return row

          return {
            ...row,
            status        : STATUS.CANCELLED,
            parent_status : STATUS.CANCELLED,
            item_status   : STATUS.CANCELLED,
            active_items  : [],
            can_cancel    : false
          }
        }))

        pageUtils?.setAlertMsg?.('Consignor Return batch successfully cancelled', ALERT_TYPES.SUCCESS)
        setIsCancelModalOpen(false)
        setCancelTarget(null)
        setCancelWarningStep(1)
      })
      .catch((error) => {
        const errorResponse = error?.response?.data || {
          messages: {
            base: error?.message || 'Unable to cancel consignor return batch'
          }
        }

        pageUtils?.setApiErrorMsg?.(errorResponse)
      })
      .finally(() => setIsLoading(false))
  }, [ cancelTarget, cancelWarningStep, isPostShipmentCancel, pageUtils ])

  const cancelTargetSkus = Array.isArray(cancelTarget?.active_items)
    ? cancelTarget.active_items.map((item) => item.sku).filter(Boolean)
    : String(cancelTarget?.product_skus || '').split(',').map((sku) => sku.trim()).filter(Boolean)
  const cancelTargetSkuText = cancelTargetSkus.length > 0 ? cancelTargetSkus.join(', ') : '-'
  const cancelTargetStatus = sellerReturnParentStatus(cancelTarget)
  const postShipmentStatusLabel = cancelTargetStatus === STATUS.SELLER_RECEIVED ? 'Seller Received' : 'Shipped'

  const cancelModalTitle = isPostShipmentCancel(cancelTarget)
    ? `Warning ${cancelWarningStep}/3 - Cancel ${postShipmentStatusLabel} Consignor Return`
    : 'Cancel Consignor Return'

  const cancelModalMessage = (() => {
    const code = cancelTarget?.code || ''

    if (!isPostShipmentCancel(cancelTarget)) {
      return `Are you sure you want to cancel Consignor Return ${code}? ${cancelTargetSkuText} — all of these active items in this batch will be cancelled.`
    }

    if (cancelWarningStep === 1) {
      return `WARNING 1/3: Consignor Return ${code} is already ${postShipmentStatusLabel}. Items: ${cancelTargetSkuText}. All of these active items in this batch will be returned to store and cancelled.`
    }

    if (cancelWarningStep === 2) {
      return 'WARNING 2/3: For each active item, inventory history will record Returned, Put Away, and Cancel Return Request. The item will be restored to its previous warehouse location.'
    }

    return `WARNING 3/3: You are about to cancel a ${postShipmentStatusLabel} Consignor Return and return its active items back to store. Are you absolutely sure you want to continue?`
  })()

  return (
    <>
      <TitlePage mainTitle={ 'Consignor Return' } subTitle={ 'List Page' } />
      <PendingTaskShortcuts tasks={ ['accepted_return'] } />

      <Card body>
        <div className='d-flex justify-content-between align-items-center flex-wrap mb-3'>
          <div className='d-flex align-items-center flex-wrap mb-2 mr-2'>
            {
              isProductCuratedRoute &&
              (
                <RedirectButton
                  color='success'
                  size='sm'
                  text={ '+ New Consignor Return' }
                  path={ `${routeBasePath}/create` }
                />
              )
            }

            <Button
              color='primary'
              type='button'
              size='sm'
              className={ isProductCuratedRoute ? 'ml-2' : '' }
              onClick={ handleDownloadCsv }
              disabled={ isDownloadingCsv }
            >
              { isDownloadingCsv ? 'Downloading...' : 'Print Return CSV' }
            </Button>
          </div>

          <form
            className='d-flex align-items-center mb-2'
            onSubmit={ handleSubmitSearch }
            style={{
              minWidth : '360px',
              maxWidth : '560px',
              width    : '100%'
            }}
          >
            <Input
              value={ searchKeyword }
              onChange={ (event) => setSearchKeyword(event.target.value) }
              placeholder='Search ID, consignor name, or product SKU'
              className='mr-2'
            />

            <Button
              color='primary'
              type='submit'
              size='sm'
              className='mr-2'
            >
              { 'Search' }
            </Button>

            <Button
              color='secondary'
              type='button'
              size='sm'
              outline
              onClick={ handleResetSearch }
            >
              { 'Reset' }
            </Button>
          </form>
        </div>

        {
          isProductCuratedRoute &&
          (
            <Alert color='info' className='mb-3'>
              <strong>{ 'Catatan pembatalan:' }</strong>
              { ' tombol Cancel di halaman List membatalkan seluruh SKU/item aktif dalam satu Consignor Return (CSR). Untuk membatalkan hanya satu SKU/item, buka detail CSR lalu gunakan tombol Cancel Item pada SKU tersebut.' }
            </Alert>
          )
        }

        <SellerOutboundReturnTable
          key={ `consignor-return-list-${tableVersion}` }
          data={ sellerOutboundReturn }
          pagination={ pagination }
          onFetchData={ handleFetchSellerOutboundReturn }
          isLoading={ isLoading }
          initialFilter={ tableFilters }
          initialPageIndex={ initialPageIndex }
          initialPageSize={ initialPageSize }
          onRowClick={ handleSelectRow }
          cancelButtonProps={{
            buttonText       : 'Cancel',
            buttonColour     : 'danger',
            isButtonDisabled : isCancelButtonDisabled,
            onButtonClick    : canCancelConsignorReturn ? handleCancelClick : null
          }}
          hiddenColumns={ canCancelConsignorReturn ? [] : [ 'action' ] }
        />
      </Card>

      <ConfirmationModal
        key={ `consignor-return-cancel-warning-${cancelWarningStep}` }
        title={ cancelModalTitle }
        isOpen={ isCancelModalOpen }
        toggle={ handleCancelModalToggle }
        handleConfirmation={ handleCancelConfirm }
        onCancel={ handleCancelAbort }
        reverseActions={ isPostShipmentCancel(cancelTarget) && cancelWarningStep === 3 }
      >
        { cancelModalMessage }
      </ConfirmationModal>
    </>
  )
}

SellerOutboundReturnListPage.propTypes = propTypes

export default SellerOutboundReturnListPage
